
import { useForm } from "react-hook-form";
import { useClubContext } from "../context/ClubContext";
import { useLabelsContext } from "../context/LabelsContext";
import { RegistrationFields } from "../models/registration-fields";
import { useConfigContext } from "../context/ConfigContext";
import { isValidDateString, isValidIsraeliID } from "../utils/helpers";
import { useCallback, useEffect, useState } from "react";
import { getFieldAvailability } from "../services/registration-service";
import { API_WARNING_CODES } from "../models/api-response";
import { ClubMember } from "../models/club-member";
import { useAuthContext } from "../context/AuthContext";

export type AvailabilityState = {
    email: {
        value: string;
        checking: boolean,
        exist: boolean,
        inactive: boolean // exists, but the member is inactive (API warning code 25)
    };
    passport: {
        value: string | null;
        checking: boolean,
        exist: boolean,
        inactive: boolean
    };
};
export const FIELDS_REQUIRING_AVAILABILITY_CHECK = ["email", "passport"] as (keyof AvailabilityState)[];

export const useRegistrationForm = () => {
    const { club } = useClubContext();
    const { labels } = useLabelsContext();
    const { config } = useConfigContext();
    const { interestedUser } = useAuthContext();
    const [availability, setAvailability] = useState<AvailabilityState>({
        email: { value: "", checking: false, exist: false, inactive: false },
        passport: { value: null, checking: false, exist: false, inactive: false },
    });
    const {
        register,
        handleSubmit,
        trigger,
        getValues,
        setError,
        setValue,
        clearErrors,
        control,
        watch,
        reset,
        getFieldState,
        formState: { errors }
    } = useForm<RegistrationFields>({
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            agreeToReceiveMails: club?.defaultAgreeToReceiveMailsChecked || false,
            showSpouseFields: club?.defaultShowSpouseFields || false
        }
    });

    const getFieldRequirement = (field: keyof RegistrationFields): "none" | "optional" | "required" => {
        switch (field) {
            case "firstName":
                return "required";
            case "lastName":
                return "required";
            case "email":
                return "required";
            case "phoneNumber":
                return "required";
            case "birthDate":
                return club?.registrationFormDateOfBirth as "none" | "optional" | "required";
            case "spouseBirthDate":
                return club?.registrationFormSpouseDateOfBirth as "none" | "optional" | "required";
            case "spouseFirstName":
                return club?.registrationFormSpouseFirstName as "none" | "optional" | "required";
            case "spouseLastName":
                return club?.registrationFormSpouseLastName as "none" | "optional" | "required";
            case "anniversary":
                return club?.registrationFormAnniversary as "none" | "optional" | "required";
            case "city":
                return club?.registrationFormCity as "none" | "optional" | "required";
            case "country":
                return club?.showCountrySelect ? "required" : "none";
            case "passport":
                return club?.showPassport ? "required" : "none";
            case "agreeToReceiveMails":
                return interestedUser?.MissingRequiredField ? "none" : club?.mandatoryAgreeToReceiveMailsChecked ? "required" : "optional";
            default:
                return "none";
        }
    }

    const registrationForm: Record<keyof RegistrationFields, any> = {
        firstName: {
            required: labels?.["RequiredField"],
            validate: (value: string) => {
                const nameRegex = new RegExp(labels?.["RegexName"] || "");
                return (nameRegex.test(value || "") || labels?.["ValidName"] || "Invalid first name");
            },
        },
        lastName: {
            required: labels?.["RequiredField"],
            validate: (value: string) => {
                const nameRegex = new RegExp(labels?.["RegexName"] || "");
                return (nameRegex.test(value || "") || labels?.["ValidName"] || "Invalid last name");
            },
        },
        email: {
            required: labels?.["RequiredField"],

            validate: async (value: string) => {
                const emailRegex = new RegExp(labels?.["MailRegex"] || "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$", "i");

                if (!emailRegex.test(value)) {
                    setFieldExist("email", false);
                    return labels?.["MailNotValid"] || "Invalid email";
                }
                if (availability.email.exist) {
                    return labels?.["ApiErrorsLabels15"] || "Email exist";
                }
            }
        },
        passport: {
            required: getFieldRequirement("passport") === "required"
                ? labels?.["RequiredField"]
                : false,
            validate: (value: string) => {
                if (getFieldRequirement("passport") !== "required") return true;

                if (!isValidIsraeliID(value)) {
                    setFieldExist("passport", false);
                    return labels?.["InvalidPassport"] || "Invalid passport";
                }
                if (availability.passport.exist) {
                    return labels?.["ExistingPassportError"];
                }
            },
        },
        phoneNumber: {
            required: labels?.["RequiredField"],
            validate: (value: number | undefined) => {
                const phonePattern = config?.language === "he" ? labels?.["RegexIsraelPhone"] : labels?.["RegexPhone"];
                const phoneRegex = new RegExp(phonePattern ?? "");

                return (phoneRegex.test(String(value) ?? "") || labels?.["ValidPhone"] || "Invalid phone");
            },
        },
        birthDate: {
            required: getFieldRequirement("birthDate") === "required"
                ? labels?.["RequiredField"]
                : false,
            validate: (value: string) => {
                if (getFieldRequirement("birthDate") === "required") {
                    if (!value || !isValidDateString(value)) {
                        return labels?.["RequiredField"];
                    }
                } else {
                    if (!value || value === "0-00-00") return true;

                    if (!isValidDateString(value)) {
                        return labels?.["InvalidBirthDateText"] || "Invalid birth date";
                    }
                }
                return true;
            },
        },
        anniversary: {
            required: getFieldRequirement("anniversary") === "required"
                ? labels?.["RequiredField"]
                : false,
            validate: (value: string) => {
                if (getFieldRequirement("anniversary") === "required") {
                    if (!value || !isValidDateString(value)) {
                        return labels?.["RequiredField"];
                    }
                } else {
                    if (!value || value === "0-00-00") return true;

                    if (!isValidDateString(value)) {
                        return labels?.["InvalidBirthDateText"] || "Invalid date";
                    }
                }
                return true;
            },
        },
        city: {
            required: getFieldRequirement("city") === "required"
                ? labels?.["RequiredField"]
                : false
        },
        country: {
            required: getFieldRequirement("country") === "required"
                ? labels?.["RequiredField"]
                : false
        },
        spouseFirstName: {
            required: getFieldRequirement("spouseFirstName") === "required"
                ? labels?.["RequiredField"]
                : false,
            validate: (value: string) => {
                if (getFieldRequirement("spouseFirstName") !== "required") return true;

                const nameRegex = new RegExp(labels?.["RegexName"] || "");
                return (nameRegex.test(value || "") || labels?.["ValidName"] || "Invalid last name");
            },
        },
        spouseLastName: {
            required: getFieldRequirement("spouseLastName") === "required"
                ? labels?.["RequiredField"]
                : false,
            validate: (value: string) => {
                if (getFieldRequirement("spouseLastName") !== "required") return true;

                const nameRegex = new RegExp(labels?.["RegexName"] || "");
                return (nameRegex.test(value || "") || labels?.["ValidName"] || "Invalid last name");
            },
        },
        spouseBirthDate: {
            required: getFieldRequirement("spouseBirthDate") === "required"
                ? labels?.["RequiredField"]
                : false,
            validate: (value: string) => {
                if (getFieldRequirement("spouseBirthDate") === "required") {
                    if (!value || !isValidDateString(value)) {
                        return labels?.["RequiredField"];
                    }
                } else {
                    if (!value || value === "0-00-00") return true;

                    if (!isValidDateString(value)) {
                        return labels?.["InvalidBirthDateText"] || "Invalid birth date";
                    }
                }
                return true;
            },
        },
        agreeToReceiveMails: {
            required: getFieldRequirement("agreeToReceiveMails") === "required"
                ? labels?.["RequiredField"]
                : false
        },
        termsAndConditions: {
            required: interestedUser?.MissingRequiredField ? false : labels?.["MustAgreeToClubTerms"] || false
        },
        showSpouseFields: {},
    };

    const setFieldChecking = (field: keyof AvailabilityState, value: any, checking: boolean): void => {
        setAvailability(prev => ({
            ...prev,
            [field]: { ...prev[field], value: String(value), checking }
        }));
    };
    const setFieldExist = (field: keyof AvailabilityState, exist: boolean, inactive: boolean = false): void => {
        setAvailability(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                exist,
                inactive
            }
        }))
    }

    const getErrorMessage = useCallback((field: keyof RegistrationFields): { key: string, message: string } | null => {
        if (!errors[field]?.message) return null;
        const errorMessage = String(errors[field]?.message ?? "");
        const labelKey = Object.keys(labels || {}).find(key => labels?.[key] === errorMessage);

        return {
            key: labelKey ?? "",
            message: errorMessage
        };
    }, [errors, labels]);

    const checkFieldAvailability = useCallback(
        async (field: keyof AvailabilityState): Promise<void> => {
            await new Promise<void>((resolve) => {
                setTimeout(async () => {
                    await trigger(field);

                    const value: string = String(getValues(field));

                    const emailRegex = new RegExp(
                        labels?.["MailRegex"] ||
                        "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
                        "i"
                    );

                    const invalidInput =
                        field === "email"
                            ? !emailRegex.test(value)
                            : !isValidIsraeliID(value);

                    if (invalidInput) {
                        resolve();
                        return;
                    }
                    setFieldChecking(field, value, true);

                    try {
                        const response = await getFieldAvailability(
                            field,
                            value,
                            config,
                            club?.bookingDomain
                        );

                        const fieldExist = !response.Data;
                        const isInactive = fieldExist && !!response.Warnings?.some(w => w.Code === API_WARNING_CODES.MemberInactive);
                        setFieldExist(field, fieldExist, isInactive);
                    } catch (err) {
                    } finally {
                        setFieldChecking(field, value, false);
                        markAsTouched(field);
                        resolve();
                    }
                }, 0);
            });
        },
        [labels, club?.bookingDomain]
    );

    const buildClubMemberRequest = (): ClubMember => {
        const form: RegistrationFields = getValues();

        return {
            GuestId: interestedUser?.GuestId ?? 0,
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phone: form.phoneNumber ? String(form.phoneNumber) : undefined,
            birthdate: form.birthDate,
            passport: form.passport ? form.passport : undefined,
            agretoMails: form.agreeToReceiveMails,
            firstNameSpouse: form.showSpouseFields ? form.spouseFirstName : null,
            lastNameSpouse: form.showSpouseFields ? form.spouseLastName : null,
            SpouseDateOfBirth: form.showSpouseFields ? form.spouseBirthDate : null,
            anniversary: form.showSpouseFields ? form.anniversary : null,
            AllowClubTerms: form.termsAndConditions,
        }
    }

    const resetForm = (): void => {
        const resetSelects = () => {
            const selects = document.querySelectorAll<HTMLSelectElement>(".club-form-selects select");

            selects.forEach((select) => {
                select.selectedIndex = 0;
                select.dispatchEvent(new Event("change", { bubbles: true }));
            });
        };
        resetSelects();
        setTimeout(() => {
            reset()
        }, 0);
    }

    const markAsTouched = (field: keyof RegistrationFields): void => {
        setValue(field, getValues(field), { shouldTouch: true });
    };

    const triggerAutoFilledField = async (
        field: keyof RegistrationFields
    ): Promise<void> => {
        const isTouched = getFieldState(field).isTouched;
        if (isTouched) return;

        if (
            FIELDS_REQUIRING_AVAILABILITY_CHECK.includes(
                field as keyof AvailabilityState,
            )
        ) {
            await checkFieldAvailability(field as keyof AvailabilityState);
        } else {
            await trigger(field);
        }
        markAsTouched(field);
    };

    useEffect(() => {
        FIELDS_REQUIRING_AVAILABILITY_CHECK.forEach((field) => {
            if (availability[field].exist) {
                setError(field, {
                    type: "exist",
                    message: availability[field].inactive
                        ? labels?.["UserInactiveError"]
                        : field === "passport"
                            ? labels?.["ExistingPassportError"]
                            : labels?.["ApiErrorsLabels15"]
                });
            } else {
                clearErrors(field);
            }
        });
    }, [availability.passport.exist, availability.email.exist, availability.passport.inactive, availability.email.inactive, labels]);

    useEffect(() => {
        if (interestedUser?.verified) {
            setValue("agreeToReceiveMails", interestedUser?.AllowMailing ?? false);
            setValue("anniversary", interestedUser?.Anniversary ?? "");
            setValue("birthDate", interestedUser?.BirthDay ?? "");
            setValue("city", interestedUser?.City ?? "");
            setValue("email", interestedUser?.Email ?? "");
            setValue("firstName", interestedUser?.FirstName ?? "");
            setValue("lastName", interestedUser?.LastName ?? "");
            setValue("passport", interestedUser?.Passport ? interestedUser?.Passport : null);
            setValue("phoneNumber", interestedUser?.Phone ?? "");
            setValue("spouseFirstName", interestedUser?.Spouse?.FirstName ?? "");
            setValue("spouseLastName", interestedUser?.Spouse?.LastName ?? "");
            setValue("spouseBirthDate", interestedUser?.Spouse?.Birthday ?? "");

            const showSpouseFields: boolean =
                !!interestedUser?.Spouse?.Birthday ||
                !!interestedUser.Spouse?.FirstName ||
                !!interestedUser.Spouse?.LastName ||
                !!interestedUser.Spouse?.Passport ||
                false;

            setValue("showSpouseFields", showSpouseFields);
        }
    }, [interestedUser]);

    return {
        registrationForm,
        handleSubmit,
        trigger,
        getValues,
        setError,
        errors,
        register,
        getFieldRequirement,
        setValue,
        control,
        clearErrors,
        availability,
        checkFieldAvailability,
        getErrorMessage,
        resetForm,
        buildClubMemberRequest,
        watch,
        triggerAutoFilledField,
        markAsTouched
    };
};